import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, errMsg, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Armchair, Copy, Send, MessageSquare, Users, CalendarDays, ArrowLeft, VideoOff, FileText, Download, Hourglass } from "lucide-react";

const getGuestId = () => {
  let gid = localStorage.getItem("guest_id");
  if (!gid) {
    gid = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("guest_id", gid);
  }
  return gid;
};

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(b / 1024)} kB`);

export default function PublicRoom() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/r\//, "").replace(/^\//, "").replace(/\/+$/, "");
  const { user } = useAuth();
  const { t } = useI18n();
  const [room, setRoom] = useState(undefined);
  const [entry, setEntry] = useState("checking");
  const [guestName, setGuestName] = useState(localStorage.getItem("guest_name") || "");
  const [nameInput, setNameInput] = useState(localStorage.getItem("guest_name") || "");
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [online, setOnline] = useState([]);
  const [events, setEvents] = useState([]);
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [videoOn, setVideoOn] = useState(false);
  const lastTs = useRef(null);
  const chatEndRef = useRef(null);
  const guestId = useRef(getGuestId());

  useEffect(() => {
    api.get(`/rooms/slug/${slug}`)
      .then(({ data }) => setRoom(data))
      .catch(() => setRoom(null));
  }, [slug]);

  const roomId = room?.id;
  const isLogged = user && user !== false;
  const authReady = user !== null;

  const tryJoin = useCallback(async (name) => {
    try {
      const { data } = await api.post(`/rooms/${roomId}/join`, { guest_id: guestId.current, guest_name: name || "" });
      setEntry(data.status);
    } catch (e) {
      setEntry("need_name");
    }
  }, [roomId]);

  useEffect(() => {
    if (!roomId || !authReady) return;
    if (isLogged) {
      tryJoin("");
    } else if (room.access_type === "public" || guestName) {
      if (room.access_type === "public" && !guestName) setEntry("need_name");
      else tryJoin(guestName);
    } else {
      setEntry("need_name");
    }
  }, [roomId, authReady, isLogged, room, guestName, tryJoin]);

  useEffect(() => {
    if (entry !== "pending" || !roomId) return;
    const gid = isLogged ? `user:${user.id}` : guestId.current;
    const int = setInterval(async () => {
      try {
        const { data } = await api.get(`/rooms/${roomId}/join-status/${gid}`);
        if (data.status === "approved" || data.status === "denied") setEntry(data.status);
      } catch {}
    }, 4000);
    return () => clearInterval(int);
  }, [entry, roomId, isLogged, user]);

  const poll = useCallback(async () => {
    if (!roomId) return;
    try {
      const { data } = await api.get(`/rooms/${roomId}/messages`, { params: lastTs.current ? { after: lastTs.current } : {} });
      if (data.length) {
        lastTs.current = data[data.length - 1].created_at;
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const fresh = data.filter((m) => !seen.has(m.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      }
    } catch {}
  }, [roomId]);

  const sendPresence = useCallback(() => {
    if (!roomId) return;
    const body = isLogged ? {} : { guest_id: guestId.current, guest_name: guestName };
    api.post(`/rooms/${roomId}/presence`, body).catch(() => {});
  }, [roomId, isLogged, guestName]);

  useEffect(() => {
    if (!roomId || entry !== "approved") return;
    poll();
    sendPresence();
    api.get(`/rooms/${roomId}/events`).then(({ data }) => setEvents(data)).catch(() => {});
    api.get(`/rooms/${roomId}/files`).then(({ data }) => setFiles(data)).catch(() => {});
    api.get(`/rooms/${roomId}/presence`).then(({ data }) => setOnline(data)).catch(() => {});
    const chatInt = setInterval(poll, 3000);
    const presInt = setInterval(() => {
      api.get(`/rooms/${roomId}/presence`).then(({ data }) => setOnline(data)).catch(() => {});
      sendPresence();
    }, 10000);
    return () => { clearInterval(chatInt); clearInterval(presInt); };
  }, [roomId, entry, poll, sendPresence]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/rooms/${roomId}/messages`, { text, guest_id: guestId.current, guest_name: guestName });
      setText("");
      poll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const submitName = (e) => {
    e.preventDefault();
    const n = nameInput.trim();
    if (!n) return;
    localStorage.setItem("guest_name", n);
    setGuestName(n);
    setEntry("checking");
    tryJoin(n);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(t("room.copied"));
  };

  if (room === undefined || !authReady) return <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center text-slate-400">…</div>;
  if (room === null)
    return (
      <div className="min-h-screen bg-[#0B0F17] flex flex-col items-center justify-center gap-4 px-6">
        <h1 className="text-3xl font-extrabold text-white">{t("public.not_found")}</h1>
        <p className="text-slate-400">{t("public.not_found_desc")} (/{slug})</p>
        <Link to="/" className="px-6 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors">{t("public.back_home")}</Link>
      </div>
    );

  const displayName = isLogged ? user.name : guestName || t("public.guest");

  if (entry === "need_name" || entry === "pending" || entry === "denied" || entry === "checking") {
    return (
      <div className="min-h-screen bg-[#0B0F17] mesh-bg grid-overlay flex items-center justify-center px-6" data-testid="room-gate">
        <div className="w-full max-w-md bg-[#131B2A] border border-slate-800 rounded-2xl p-8 text-center">
          <span className="w-12 h-12 rounded-xl mx-auto block" style={{ backgroundColor: room.color }} />
          <h1 className="mt-4 text-xl font-bold text-white">{room.name}</h1>
          <span className="font-mono text-xs text-indigo-300 block mt-1">digisidlo.cz/{room.full_slug}</span>
          <span className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${room.owner_online ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-slate-700/40 text-slate-400 border border-slate-700"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${room.owner_online ? "bg-emerald-400 live-dot" : "bg-slate-500"}`} />
            {room.owner_online ? t("public.owner_online") : t("public.owner_offline")}
          </span>
          {entry === "need_name" && (
            <form onSubmit={submitName} className="mt-6 space-y-3">
              <p className="text-sm text-slate-400">{t("public.guest_title")}</p>
              <input data-testid="guest-name-input" value={nameInput} onChange={(e) => setNameInput(e.target.value)}
                placeholder={t("public.guest_name_ph")}
                className="w-full px-4 py-3 rounded-xl bg-[#0B0F17] border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500" />
              <button data-testid="guest-enter-button" className="w-full py-3 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-500 transition-colors">
                {t("public.guest_enter")}
              </button>
            </form>
          )}
          {entry === "pending" && (
            <div className="mt-6" data-testid="waiting-room-screen">
              <Hourglass className="w-8 h-8 text-amber-400 mx-auto animate-pulse" />
              <h2 className="mt-3 font-bold text-white">{t("public.waiting_title")}</h2>
              <p className="mt-2 text-sm text-slate-400">{t("public.waiting_desc")}</p>
            </div>
          )}
          {entry === "denied" && <p className="mt-6 text-sm text-red-400" data-testid="entry-denied">{t("public.denied")}</p>}
          {entry === "checking" && <p className="mt-6 text-sm text-slate-500">…</p>}
        </div>
      </div>
    );
  }

  const jitsiUrl = `https://meet.jit.si/digisidlo-${room.full_slug.replace(/\//g, "-")}#userInfo.displayName=%22${encodeURIComponent(displayName)}%22&config.prejoinConfig.enabled=false&config.startWithAudioMuted=true`;

  const tabs = [
    { id: "chat", label: t("public.tab_chat"), icon: MessageSquare },
    { id: "people", label: `${t("public.tab_people")} (${online.length})`, icon: Users },
    { id: "program", label: t("public.tab_program"), icon: CalendarDays },
    { id: "files", label: t("public.tab_files"), icon: FileText },
  ];

  const REC = { none: "", daily: ` · ${t("manage.rec_daily")}`, weekly: ` · ${t("manage.rec_weekly")}`, monthly: ` · ${t("manage.rec_monthly")}` };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex flex-col" data-testid="public-room-page">
      <header className="glass-nav h-16 flex items-center px-4 sm:px-6 gap-3 shrink-0 sticky top-0 z-40" style={{ borderBottom: `2px solid ${room.color}` }}>
        <Link to={isLogged ? "/dashboard" : "/"} data-testid="room-back-link" className="text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <span className="w-8 h-8 rounded-lg shrink-0" style={{ backgroundColor: room.color }} />
        <div className="min-w-0 flex-1">
          <h1 className="font-heading font-bold text-white truncate leading-tight text-sm sm:text-base">{room.name}</h1>
          <span className="font-mono text-[11px] text-indigo-300 block truncate">digisidlo.cz/{room.full_slug}</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <span className={`hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${room.owner_online ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-slate-700/40 text-slate-400 border border-slate-700"}`} data-testid="owner-status-badge">
            <span className={`w-1.5 h-1.5 rounded-full ${room.owner_online ? "bg-emerald-400 live-dot" : "bg-slate-500"}`} />
            {room.owner_online ? t("public.owner_online") : t("public.owner_offline")}
          </span>
          <span className="hidden md:flex items-center gap-1.5 text-xs text-slate-400">
            <Armchair className="w-3.5 h-3.5" /> {room.capacity}
          </span>
          <button onClick={copyLink} data-testid="room-copy-link-button"
            className="p-2 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-indigo-500 transition-colors">
            <Copy className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        <div className="flex-1 bg-black relative min-h-[45vh] lg:min-h-0" data-testid="jitsi-video-container">
          {videoOn ? (
            <iframe
              title="Jitsi Meet"
              src={jitsiUrl}
              className="absolute inset-0 w-full h-full border-0"
              allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 mesh-bg grid-overlay">
              <span className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${room.color}22`, border: `1px solid ${room.color}66` }}>
                <VideoOff className="w-7 h-7" style={{ color: room.color }} />
              </span>
              <div className="text-center px-6">
                <h2 className="text-lg font-bold text-white">{t("public.video_ready")}</h2>
                <p className="mt-1 text-sm text-slate-400 max-w-sm">{t("public.video_desc")}</p>
              </div>
              <button data-testid="join-video-button" onClick={() => setVideoOn(true)}
                className="px-7 py-3 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-500 transition-colors">
                {t("public.join_video")}
              </button>
              <p className="text-[11px] text-slate-600 max-w-xs text-center px-4">{t("public.jitsi_note")}</p>
            </div>
          )}
        </div>

        <aside className="w-full lg:w-[380px] shrink-0 border-t lg:border-t-0 lg:border-l border-slate-800 bg-[#0E1420] flex flex-col h-[50vh] lg:h-auto">
          <div className="flex border-b border-slate-800 shrink-0">
            {tabs.map((x) => (
              <button key={x.id} data-testid={`room-tab-${x.id}`} onClick={() => setTab(x.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold transition-colors ${tab === x.id ? "text-white border-b-2 border-indigo-500" : "text-slate-500 hover:text-slate-300"}`}>
                <x.icon className="w-3.5 h-3.5" /> {x.label}
              </button>
            ))}
          </div>

          {tab === "chat" && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                {messages.length === 0 && <p className="text-xs text-slate-600 text-center mt-8">{t("public.no_messages")}</p>}
                {messages.map((m) => (
                  <div key={m.id} className="text-sm">
                    <span className="font-semibold" style={{ color: isLogged && m.user_id === user.id ? room.color : "#94A3B8" }}>{m.user_name}</span>
                    <span className="ml-2 text-[10px] text-slate-600">{new Date(m.created_at).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}</span>
                    <p className="text-slate-200 break-words">{m.text}</p>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-slate-800 flex gap-2 shrink-0">
                <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)}
                  placeholder={t("public.msg_ph")}
                  className="flex-1 px-4 py-2.5 rounded-full bg-[#0B0F17] border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500" />
                <button data-testid="chat-send-button" className="p-2.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-500 transition-colors">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}

          {tab === "people" && (
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="online-list">
              {online.length === 0 && <p className="text-xs text-slate-600 text-center mt-8">{t("public.no_online")}</p>}
              {online.filter((p) => p.user_id !== undefined).map((p) => (
                <div key={p.user_id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#131B2A] border border-slate-800">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 live-dot" />
                  <span className="text-sm text-white">{p.user_name || t("public.guest")}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "program" && (
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="program-list">
              {events.length === 0 && <p className="text-xs text-slate-600 text-center mt-8">{t("public.no_program")}</p>}
              {events.map((e) => (
                <div key={e.id} className="px-4 py-3 rounded-xl bg-[#131B2A] border border-slate-800">
                  <div className="text-sm font-semibold text-white">{e.title}</div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {new Date(e.start_time).toLocaleString("cs-CZ")} · {e.duration_minutes} {t("public.min")}{REC[e.recurrence]}
                  </div>
                  {e.description && <p className="mt-1 text-xs text-slate-400">{e.description}</p>}
                </div>
              ))}
            </div>
          )}

          {tab === "files" && (
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="room-files-list">
              {files.length === 0 && <p className="text-xs text-slate-600 text-center mt-8">{t("public.no_files")}</p>}
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#131B2A] border border-slate-800">
                  <FileText className="w-4 h-4 text-indigo-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{f.original_filename}</div>
                    <div className="text-[10px] text-slate-500">{fmtSize(f.size)} · {f.uploader_name}</div>
                  </div>
                  <a href={`${API}/files/${f.id}/download`} data-testid={`file-download-${f.id}`}
                    className="p-2 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-indigo-500 transition-colors">
                    <Download className="w-3.5 h-3.5" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
